import React, { useMemo } from 'react';
import type { SubscriptionPlan } from '../types/subscription';
import { useSubscription } from '../hooks/useSubscription';

interface ReplayUpgradePromptProps {
  isOpen: boolean;
  onClose: () => void;
  onUpgrade: () => void;
  currentPlan: SubscriptionPlan;
  feature: 'replay' | 'tickReplay';
}

const ReplayUpgradePrompt: React.FC<ReplayUpgradePromptProps> = ({
  isOpen,
  onClose,
  onUpgrade,
  currentPlan: _currentPlan,
  feature,
}) => {
  const { availablePlans } = useSubscription();
  
  const isTickReplay = feature === 'tickReplay';
  
  // Find the cheapest plan that has a price > 0 (not free)
  const cheapestPaidPlan = useMemo(() => {
    const paidPlans = availablePlans.filter(p => p.price > 0);
    if (paidPlans.length === 0) return null;
    return paidPlans.reduce((cheapest, plan) => 
      plan.price < cheapest.price ? plan : cheapest
    );
  }, [availablePlans]);
  
  // Use the cheapest plan's data or fallback
  const requiredPlan = cheapestPaidPlan?.name || 'Essential';
  const planPrice = cheapestPaidPlan?.price || 14.95;
  const planFeatures = cheapestPaidPlan?.features || [];
  
  // currentPlan can be used for future enhancements like showing upgrade path
  void _currentPlan;
  
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
      {/* Backdrop with blur */}
      <div 
        className="absolute inset-0 bg-black/70 backdrop-blur-md"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="relative w-full max-w-lg bg-gradient-to-b from-slate-900 via-slate-900 to-slate-950 border border-slate-700/50 rounded-3xl shadow-2xl overflow-hidden animate-fade-in-up">
        {/* Decorative gradient */}
        <div className="absolute top-0 left-0 right-0 h-32 bg-gradient-to-b from-blue-500/20 via-indigo-500/10 to-transparent pointer-events-none" />
        
        {/* Close button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 p-2 text-slate-400 hover:text-white hover:bg-slate-800/50 rounded-xl transition-colors z-10"
        >
          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>

        {/* Content */}
        <div className="relative p-6 sm:p-8">
          {/* Icon */}
          <div className="flex justify-center mb-6">
            <div className="relative">
              <div className="w-20 h-20 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-2xl flex items-center justify-center shadow-lg shadow-blue-500/30">
                {isTickReplay ? (
                  <svg className="w-10 h-10 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M13 10V3L4 14h7v7l9-11h-7z" />
                  </svg>
                ) : (
                  <svg className="w-10 h-10 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                )}
              </div>
              {/* Lock badge */}
              <div className="absolute -bottom-2 -right-2 w-8 h-8 bg-amber-500 rounded-full flex items-center justify-center shadow-lg">
                <svg className="w-4 h-4 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                </svg>
              </div>
            </div>
          </div>

          {/* Title */}
          <h2 className="text-2xl sm:text-3xl font-bold text-center text-white mb-3">
            {isTickReplay ? 'Unlock Tick-by-Tick Replay' : 'Unlock Replay Mode'}
          </h2>

          {/* Description */}
          <p className="text-center text-slate-400 mb-6 leading-relaxed">
            {isTickReplay 
              ? 'Experience the most realistic trading simulation with tick-by-tick market replay. Practice your strategies with real market microstructure.'
              : 'Travel back in time and practice trading with historical market data. Perfect your strategies without risking real money.'
            }
          </p>

          {/* Features */}
          <div className="bg-slate-800/50 rounded-2xl p-4 mb-6">
            <h4 className="text-sm font-semibold text-slate-300 mb-3 flex items-center gap-2">
              <svg className="w-4 h-4 text-blue-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
              What you'll get with {requiredPlan}
            </h4>
            <ul className="space-y-2">
              {planFeatures.length > 0 ? (
                planFeatures.slice(0, 4).map((feature, idx) => (
                  <li key={idx} className="flex items-center gap-2 text-sm text-slate-300">
                    <span className="w-1.5 h-1.5 bg-green-400 rounded-full" />
                    {feature.name}
                  </li>
                ))
              ) : (
                <>
                  <li className="flex items-center gap-2 text-sm text-slate-300">
                    <span className="w-1.5 h-1.5 bg-green-400 rounded-full" />
                    Candle-by-candle replay
                  </li>
                  <li className="flex items-center gap-2 text-sm text-slate-300">
                    <span className="w-1.5 h-1.5 bg-green-400 rounded-full" />
                    Paper trading simulation
                  </li>
                  <li className="flex items-center gap-2 text-sm text-slate-300">
                    <span className="w-1.5 h-1.5 bg-green-400 rounded-full" />
                    Historical data access
                  </li>
                  <li className="flex items-center gap-2 text-sm text-slate-300">
                    <span className="w-1.5 h-1.5 bg-green-400 rounded-full" />
                    2x2 multi-chart layout
                  </li>
                </>
              )}
            </ul>
          </div>

          {/* Pricing teaser */}
          <div className="text-center mb-6">
            <span className="text-slate-500 text-sm">Starting at just</span>
            <div className="text-3xl font-bold text-white">
              ${planPrice.toFixed(2)}
              <span className="text-lg text-slate-400 font-normal">/month</span>
            </div>
          </div>

          {/* CTA Buttons */}
          <div className="space-y-3">
            <button
              onClick={onUpgrade}
              className="w-full py-4 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-500 hover:to-indigo-500 rounded-xl text-white font-bold text-lg shadow-lg shadow-blue-500/30 transition-all transform hover:scale-[1.02] active:scale-[0.98]"
            >
              View Plans & Upgrade
            </button>
            <button
              onClick={onClose}
              className="w-full py-3 text-slate-400 hover:text-white hover:bg-slate-800/50 rounded-xl transition-colors text-sm"
            >
              Maybe Later
            </button>
          </div>

          {/* Trust badges */}
          <div className="flex items-center justify-center gap-4 mt-6 pt-6 border-t border-slate-800">
            <div className="flex items-center gap-1.5 text-xs text-slate-500">
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
              </svg>
              Secure Payment
            </div>
            <div className="flex items-center gap-1.5 text-xs text-slate-500">
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
              Cancel Anytime
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ReplayUpgradePrompt;
